/** SUNTRONIX ONE — Tenant portal: Ship-to Location Master (tenant admin). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Plus, Trash2, Loader2, Save } from "lucide-react";

const EMPTY = { label: "", address: "", city: "", state: "", pincode: "", contact_name: "", contact_phone: "" };

function LocDialog({ loc, onClose, onSaved }) {
  const [f, setF] = useState(loc.id ? loc : { ...EMPTY });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.label.trim() || !f.address.trim()) { toast.error("Label and address are required"); return; }
    setBusy(true);
    try {
      if (loc.id) await api.patch(`/tenant-catalogue/locations/${loc.id}`, f);
      else await api.post("/tenant-catalogue/locations", f);
      toast.success("Address saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="location-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-sky-600" />{loc.id ? "Edit" : "New"} address</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Input placeholder="Label (e.g. Chennai HO)" value={f.label} onChange={(e) => set("label", e.target.value)} data-testid="loc-label" className="h-9" />
          <Input placeholder="Address" value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="loc-address" className="h-9" />
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="City" value={f.city} onChange={(e) => set("city", e.target.value)} className="h-9" />
            <Input placeholder="State" value={f.state} onChange={(e) => set("state", e.target.value)} className="h-9" />
            <Input placeholder="Pincode" value={f.pincode} onChange={(e) => set("pincode", e.target.value)} className="h-9" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Contact name" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="h-9" />
            <Input placeholder="Contact phone" value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="h-9" />
          </div>
          <Button onClick={save} disabled={busy} data-testid="loc-save" className="w-full bg-sky-600 hover:bg-sky-700 text-white">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MyLocations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/tenant-catalogue/locations"); setItems(data.items); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { if (!window.confirm("Delete this address?")) return; await api.delete(`/tenant-catalogue/locations/${id}`); load(); };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-5" data-testid="my-locations-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Account</div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><MapPin className="h-6 w-6 text-sky-600" />Delivery Addresses</h1>
          <p className="text-sm text-slate-500">Approved ship-to locations for checkout.</p>
        </div>
        <Button onClick={() => setEdit({})} data-testid="loc-new" className="bg-sky-600 hover:bg-sky-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Add address</Button>
      </div>
      {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div> : (
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
          {items.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No addresses yet.</div>}
          {items.map((l) => (
            <div key={l.id} data-testid={`location-${l.id}`} className="flex items-center gap-3 px-4 py-3">
              <MapPin className="h-4 w-4 text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800">{l.label}</div>
                <div className="text-[12px] text-slate-500">{l.address}{l.city ? `, ${l.city}` : ""}{l.pincode ? ` - ${l.pincode}` : ""}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setEdit(l)}>Edit</Button>
              <button onClick={() => del(l.id)} className="text-rose-500 p-1" data-testid={`location-del-${l.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      {edit && <LocDialog loc={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
